import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Building, User, Calendar, MapPin } from 'lucide-react';
import { ComplianceBadge } from './ComplianceBadge';
import { routes } from '@/lib/routes';
import type { IntakeListItem } from '@/types/api';

interface IntakeCardProps {
  intake: IntakeListItem;
}

export function IntakeCard({ intake }: IntakeCardProps) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case 'complete':
      case 'approved':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'in_progress':
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'draft':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  return (
    <Link
      to={routes.intakeDetail(intake.recordId)}
      className="block p-6 bg-white border border-border rounded-lg hover:shadow-md transition-shadow"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-lg text-foreground mb-1">
            {intake.fullAddress || 'No Address'}
          </h3>
          {intake.recId && (
            <p className="text-sm text-muted-foreground">
              ID: {intake.recId}
            </p>
          )}
        </div>
        <ComplianceBadge compliance={intake.compliance} size="sm" />
      </div>

      {/* Transaction Type & Status */}
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded">
          <Building className="h-3 w-3" />
          {intake.transactionType || 'Unknown'}
        </span>
        {intake.status && (
          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium border rounded ${getStatusColor(intake.status)}`}>
            {intake.status}
          </span>
        )}
      </div>

      {/* Client & Agent */}
      <div className="space-y-2 mb-4">
        {intake.primaryClientName && (
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{intake.primaryClientName}</span>
          </div>
        )}
        {intake.agentName && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <span>Agent: {intake.agentName}</span>
          </div>
        )}
      </div>

      {/* Timeline & Submitted */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        {intake.timeline && (
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>Timeline: {intake.timeline}</span>
          </div>
        )}
        <span>
          Submitted {formatDate(intake.submittedAt)}
        </span>
      </div>
    </Link>
  );
}